// src/modules/CqcOperationalCompliance.tsx
import React, { useState, useEffect } from 'react';
import {
  Activity, AlertTriangle, CheckCircle, Clock, FileText, Users,
  BookOpen, Shield, Clipboard, Calendar, HardDrive, Phone,
  FileCheck, FileWarning, Award, Crown, Lock, Unlock, Plus,
  Trash2, Edit, Save, X, Eye, EyeOff, Upload, Image,
  TrendingUp, TrendingDown, BarChart3, PieChart, Zap
} from 'lucide-react';
import {
  Audit, TrainingRecord, Policy, Risk, Meeting, EquipmentInspection, Complaint,
  AuditStatus, TrainingStatus, PolicyStatus, RiskSeverity, ComplaintStatus,
  CompetencyActionPlan, PlanStatus, Incident, LocalAuthority
} from '../types/compliance';
import { generateCqcRegulation18Payload } from '../utils/notificationGenerator';
import { getEscalationDetails } from '../utils/escalationHelper';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RePieChart, Pie, Cell
} from 'recharts';

// ─── HELPERS ──────────────────────────────────────────────

const formatDate = (date: string) => new Date(date).toLocaleDateString();
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

// ─── SUB-COMPONENTS ──────────────────────────────────────

const AuditsTab: React.FC = () => {
  const [audits, setAudits] = useState<Audit[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_audits');
    if (stored) setAudits(JSON.parse(stored));
  }, []);

  const getStatusColor = (status: AuditStatus) => {
    switch(status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Audits & Inspections</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Category</th>
              <th className="p-2 text-left">Scheduled</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {audits.length === 0 ? (
              <tr><td colSpan={5} className="p-4 text-center text-gray-400">No audits found</td></tr>
            ) : (
              audits.map(a => (
                <tr key={a.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{a.title}</td>
                  <td className="p-2">{a.category}</td>
                  <td className="p-2">{formatDate(a.scheduledDate)}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(a.status)}`}>{a.status}</span></td>
                  <td className="p-2">
                    <button className="text-[#123B36] text-xs hover:underline">View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TrainingTab: React.FC = () => {
  const [training, setTraining] = useState<TrainingRecord[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_training');
    if (stored) setTraining(JSON.parse(stored));
  }, []);

  const getStatusColor = (status: TrainingStatus) => {
    switch(status) {
      case 'CURRENT': return 'bg-green-100 text-green-800';
      case 'EXPIRING_SOON': return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED': return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Staff Training & Competency</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Staff</th>
              <th className="p-2 text-left">Course</th>
              <th className="p-2 text-left">Expiry</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {training.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-center text-gray-400">No training records</td></tr>
            ) : (
              training.map(t => (
                <tr key={t.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{t.staffName}</td>
                  <td className="p-2">{t.courseName}</td>
                  <td className="p-2">{formatDate(t.expiryDate)}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(t.status)}`}>{t.status}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PoliciesTab: React.FC = () => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_policies');
    if (stored) setPolicies(JSON.parse(stored));
  }, []);

  const getStatusColor = (status: PolicyStatus) => {
    switch(status) {
      case 'CURRENT': return 'bg-green-100 text-green-800';
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'ARCHIVED': return 'bg-gray-300 text-gray-600';
      case 'OVERDUE': return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Policy Register</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Title</th>
              <th className="p-2 text-left">Category</th>
              <th className="p-2 text-left">Next Review</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {policies.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-center text-gray-400">No policies</td></tr>
            ) : (
              policies.map(p => (
                <tr key={p.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{p.title}</td>
                  <td className="p-2">{p.category}</td>
                  <td className="p-2">{formatDate(p.nextReview)}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(p.status)}`}>{p.status}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RisksTab: React.FC = () => {
  const [risks, setRisks] = useState<Risk[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_risks');
    if (stored) setRisks(JSON.parse(stored));
  }, []);

  const getSeverityColor = (severity: RiskSeverity) => {
    switch(severity) {
      case 'LOW': return 'bg-blue-100 text-blue-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'CRITICAL': return 'bg-red-100 text-red-800';
    }
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Risk Register</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-left">Category</th>
              <th className="p-2 text-left">Severity</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {risks.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-center text-gray-400">No risks</td></tr>
            ) : (
              risks.map(r => (
                <tr key={r.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{r.description}</td>
                  <td className="p-2">{r.category}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${getSeverityColor(r.severity)}`}>{r.severity}</span></td>
                  <td className="p-2">{r.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MeetingsTab: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_meetings');
    if (stored) setMeetings(JSON.parse(stored));
  }, []);

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Governance & Meetings</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Meeting</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {meetings.length === 0 ? (
              <tr><td colSpan={3} className="p-4 text-center text-gray-400">No meetings</td></tr>
            ) : (
              meetings.map(m => (
                <tr key={m.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{m.title}</td>
                  <td className="p-2">{formatDate(m.date)}</td>
                  <td className="p-2">{m.status}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EquipmentTab: React.FC = () => {
  const [equipment, setEquipment] = useState<EquipmentInspection[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_equipment');
    if (stored) setEquipment(JSON.parse(stored));
  }, []);

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'PASS': return 'bg-green-100 text-green-800';
      case 'FAIL': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Equipment & Premises Safety</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Equipment</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-left">Last Inspection</th>
              <th className="p-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {equipment.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-center text-gray-400">No equipment records</td></tr>
            ) : (
              equipment.map(e => (
                <tr key={e.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{e.equipmentName}</td>
                  <td className="p-2">{e.inspectionType}</td>
                  <td className="p-2">{formatDate(e.lastInspection)}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(e.status)}`}>{e.status}</span></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ComplaintsTab: React.FC = () => {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_complaints');
    if (stored) setComplaints(JSON.parse(stored));
  }, []);

  const getStatusColor = (status: ComplaintStatus) => {
    switch(status) {
      case 'RECEIVED': return 'bg-gray-100 text-gray-800';
      case 'INVESTIGATING': return 'bg-yellow-100 text-yellow-800';
      case 'RESOLVED': return 'bg-blue-100 text-blue-800';
      case 'CLOSED': return 'bg-green-100 text-green-800';
    }
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Complaints & Concerns</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Resident</th>
              <th className="p-2 text-left">Complainant</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {complaints.length === 0 ? (
              <tr><td colSpan={4} className="p-4 text-center text-gray-400">No complaints</td></tr>
            ) : (
              complaints.map(c => (
                <tr key={c.id} className="border-b border-[#E1DCCD]">
                  <td className="p-2">{c.residentName}</td>
                  <td className="p-2">{c.complainant}</td>
                  <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(c.investigationStatus)}`}>{c.investigationStatus}</span></td>
                  <td className="p-2">{formatDate(c.dateReceived)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── INCIDENTS TAB (with CQC Notification & Escalation) ───

const IncidentsTab: React.FC = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_incidents');
    if (stored) setIncidents(JSON.parse(stored));
  }, []);

  const generateNotification = (incident: Incident) => {
    if (!incident.resident) return;
    const payload = generateCqcRegulation18Payload({
      residentCode: incident.resident.identityCode,
      localAuthority: incident.resident.localAuthority,
      incidentText: incident.description,
      immediateActions: incident.immediateActionsTaken,
      rootCause: incident.rootCauseAnalysis || 'Under investigation',
      correctiveActions: incident.actionPlanItems?.map(t => t.correctiveActionPlan).join('\n') || 'Pending',
    });
    navigator.clipboard.writeText(payload).then(() => alert('CQC notification copied to clipboard!'));
  };

  return (
    <div>
      <h4 className="font-bold text-[#123B36] mb-4">Incidents</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F5EE]">
            <tr>
              <th className="p-2 text-left">Ref</th>
              <th className="p-2 text-left">Resident</th>
              <th className="p-2 text-left">Council</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Escalation</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {incidents.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-center text-gray-400">No incidents</td></tr>
            ) : (
              incidents.map(inc => {
                const esc = inc.resident ? getEscalationDetails(inc.resident.localAuthority) : null;
                return (
                  <tr key={inc.id} className="border-b border-[#E1DCCD]">
                    <td className="p-2">{inc.incidentRef}</td>
                    <td className="p-2">{inc.resident?.identityCode}</td>
                    <td className="p-2">{inc.resident?.localAuthority}</td>
                    <td className="p-2">{inc.status}</td>
                    <td className="p-2">
                      {esc && <span className="text-xs text-[#6B7A76]">{esc.hub}</span>}
                    </td>
                    <td className="p-2">
                      <button
                        onClick={() => generateNotification(inc)}
                        className="bg-[#123B36] text-white px-2 py-1 rounded text-xs hover:bg-[#0E2C28]"
                      >
                        CQC Notify
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── ANALYTICS TAB (with charts) ───────────────────────────

const AnalyticsTab: React.FC = () => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [actionPlans, setActionPlans] = useState<CompetencyActionPlan[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);

  useEffect(() => {
    const storedSessions = localStorage.getItem('rmacs_competency_sessions');
    if (storedSessions) setSessions(JSON.parse(storedSessions));
    const storedPlans = localStorage.getItem('rmacs_competency_remediation');
    if (storedPlans) setActionPlans(JSON.parse(storedPlans));
    const storedIncidents = localStorage.getItem('rmacs_compliance_incidents');
    if (storedIncidents) setIncidents(JSON.parse(storedIncidents));
  }, []);

  // ─── Daily ──────────────────────────────────────────────
  const today = new Date().toDateString();
  const interviewsToday = sessions.filter(s => new Date(s.date_executed).toDateString() === today).length;
  const nearDeadline = actionPlans.filter(p => {
    const now = new Date();
    const chase = new Date(p.chase_deadline);
    const diff = (chase.getTime() - now.getTime()) / (1000 * 60 * 60);
    return diff > 0 && diff <= 4 && !p.actual_chased_at;
  }).length;

  // ─── Weekly ─────────────────────────────────────────────
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const weekSessions = sessions.filter(s => new Date(s.date_executed) >= weekAgo);
  const weekPass = weekSessions.filter(s => s.session_status === 'Fully Compliant (Pass)').length;
  const weekTotal = weekSessions.length;
  const passRate = weekTotal > 0 ? (weekPass / weekTotal * 100) : 0;

  const resolvedPlans = actionPlans.filter(p => p.plan_status === 'Successfully Resolved & Closed' && p.actual_completed_at);
  const totalDays = resolvedPlans.reduce((sum, p) => {
    const created = new Date(p.created_at);
    const completed = new Date(p.actual_completed_at!);
    return sum + (completed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  }, 0);
  const meanDays = resolvedPlans.length > 0 ? (totalDays / resolvedPlans.length) : 0;

  // ─── Monthly ────────────────────────────────────────────
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const monthSessions = sessions.filter(s => new Date(s.date_executed) >= monthAgo);
  const monthPass = monthSessions.filter(s => s.session_status === 'Fully Compliant (Pass)').length;
  const monthTotal = monthSessions.length;
  const monthPassRate = monthTotal > 0 ? (monthPass / monthTotal * 100) : 0;
  const belowThreshold = monthPassRate < 90;

  // ─── Incident category chart data ─────────────────────
  const incidentCategories = incidents.reduce((acc: any, inc) => {
    const cat = inc.status || 'Other';
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  const chartData = Object.keys(incidentCategories).map(key => ({ name: key, count: incidentCategories[key] }));

  // ─── Colors for pie ────────────────────────────────────
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <div className="space-y-6">
      {/* Daily */}
      <div className="border-b border-[#E1DCCD] pb-4">
        <h4 className="font-bold text-[#123B36] flex items-center gap-2"><Clock className="w-5 h-5" /> Daily Analytics</h4>
        <div className="grid grid-cols-3 gap-4 mt-2">
          <div className="bg-blue-50 p-3 rounded-lg text-center"><p className="text-sm text-gray-500">Interviews Today</p><p className="text-2xl font-bold">{interviewsToday}</p></div>
          <div className="bg-green-50 p-3 rounded-lg text-center"><p className="text-sm text-gray-500">Checks Today</p><p className="text-2xl font-bold">0</p></div>
          <div className={`p-3 rounded-lg text-center ${nearDeadline > 0 ? 'bg-red-100 border border-red-300' : 'bg-yellow-50'}`}>
            <p className="text-sm text-gray-500">Deadline ≤ 4h</p><p className="text-2xl font-bold">{nearDeadline}</p>
            {nearDeadline > 0 && <p className="text-xs text-red-600 font-semibold">⚠️ Action required!</p>}
          </div>
        </div>
      </div>

      {/* Weekly */}
      <div className="border-b border-[#E1DCCD] pb-4">
        <h4 className="font-bold text-[#123B36] flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Weekly Analytics</h4>
        <div className="grid grid-cols-3 gap-4 mt-2">
          <div className="bg-gray-50 p-3 rounded-lg text-center"><p className="text-sm text-gray-500">Pass Rate</p><p className="text-2xl font-bold">{passRate.toFixed(1)}%</p></div>
          <div className="bg-gray-50 p-3 rounded-lg text-center col-span-2"><p className="text-sm text-gray-500">Mean Days to Resolution</p><p className="text-2xl font-bold">{meanDays.toFixed(1)} days</p></div>
        </div>
      </div>

      {/* Monthly */}
      <div className="border-b border-[#E1DCCD] pb-4">
        <h4 className="font-bold text-[#123B36] flex items-center gap-2"><PieChart className="w-5 h-5" /> Monthly Board Review</h4>
        <div className="mt-2 bg-[#F7F5EE] p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <div><p className="text-sm text-gray-500">Pass Rate</p><p className="text-2xl font-bold">{monthPassRate.toFixed(1)}%</p></div>
            {belowThreshold && (
              <div className="bg-red-100 border border-red-300 p-3 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <span className="text-red-700 font-bold">⚠️ Board Alert: Regional IPC Audit Risk Identified in Housekeeping Team</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart: Incident Categories */}
      <div>
        <h4 className="font-bold text-[#123B36] mb-2 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Incident Trends</h4>
        {chartData.length === 0 ? (
          <p className="text-gray-400 text-sm">No incident data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#123B36" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ─── ESCALATION ENGINE ──────────────────────────────────

function useEscalationEngine() {
  useEffect(() => {
    const interval = setInterval(() => {
      const plans: CompetencyActionPlan[] = JSON.parse(localStorage.getItem('rmacs_competency_remediation') || '[]');
      let updated = false;
      const now = new Date();
      const newPlans = plans.map(p => {
        let newStatus = p.plan_status;
        if (!p.actual_chased_at && new Date(p.chase_deadline) < now && p.plan_status !== 'Chasing Triggered') {
          newStatus = 'Chasing Triggered';
          updated = true;
        }
        if (!p.actual_completed_at && !p.monthly_governance_signoff.is_signed_off && new Date(p.closure_deadline) < now && p.plan_status !== 'Overdue Breach') {
          newStatus = 'Overdue Breach';
          updated = true;
        }
        return { ...p, plan_status: newStatus };
      });
      if (updated) {
        localStorage.setItem('rmacs_competency_remediation', JSON.stringify(newPlans));
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);
}

// ─── MAIN COMPONENT ──────────────────────────────────────

export function CqcOperationalCompliance() {
  const [activeSubTab, setActiveSubTab] = useState('analytics');

  useEscalationEngine();

  const subTabs = [
    { id: 'analytics', label: 'Analytics', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'incidents', label: 'Incidents', icon: <Activity className="w-4 h-4" /> },
    { id: 'audits', label: 'Audits', icon: <FileCheck className="w-4 h-4" /> },
    { id: 'training', label: 'Training', icon: <Users className="w-4 h-4" /> },
    { id: 'policies', label: 'Policies', icon: <BookOpen className="w-4 h-4" /> },
    { id: 'risks', label: 'Risks', icon: <AlertTriangle className="w-4 h-4" /> },
    { id: 'meetings', label: 'Governance', icon: <Calendar className="w-4 h-4" /> },
    { id: 'equipment', label: 'Equipment', icon: <HardDrive className="w-4 h-4" /> },
    { id: 'complaints', label: 'Complaints', icon: <Phone className="w-4 h-4" /> },
  ];

  return (
    <div>
      <div className="flex gap-2 border-b border-[#E1DCCD] pb-2 mb-4 flex-wrap">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 ${
              activeSubTab === tab.id ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36] hover:bg-[#E1DCCD]'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-4">
        {activeSubTab === 'analytics' && <AnalyticsTab />}
        {activeSubTab === 'incidents' && <IncidentsTab />}
        {activeSubTab === 'audits' && <AuditsTab />}
        {activeSubTab === 'training' && <TrainingTab />}
        {activeSubTab === 'policies' && <PoliciesTab />}
        {activeSubTab === 'risks' && <RisksTab />}
        {activeSubTab === 'meetings' && <MeetingsTab />}
        {activeSubTab === 'equipment' && <EquipmentTab />}
        {activeSubTab === 'complaints' && <ComplaintsTab />}
      </div>
    </div>
  );
}